import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, MapPin, Camera, X, CheckCircle2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const TYPES = [
  { v: "falta_insumos", l: "Falta de insumos (papel, jabón, etc.)" },
  { v: "dano", l: "Daño en instalaciones" },
  { v: "basura", l: "Basura excesiva" },
  { v: "fuga", l: "Fuga de agua o líquidos" },
  { v: "mal_olor", l: "Mal olor" },
  { v: "acceso", l: "Acceso bloqueado o restringido" },
  { v: "otro", l: "Otro problema" },
];

export default function PublicReportPage() {
  const { areaId } = useParams();
  const [info, setInfo] = useState(null);
  const [type, setType] = useState("falta_insumos");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/areas/${areaId}`);
        setInfo(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "No se pudo cargar el área");
      }
    })();
  }, [areaId]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPhotoPreview(URL.createObjectURL(f));
    setUploading(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await publicApi.post(`/public/uploads/incident-photo/${areaId}`, fd);
      setPhotoUrl(data.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error subiendo la foto");
      setPhotoPreview(null);
    } finally { setUploading(false); }
  };

  const removePhoto = () => { setPhotoPreview(null); setPhotoUrl(null); if (inputRef.current) inputRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10) {
      setErr("La descripción debe tener al menos 10 caracteres."); return;
    }
    setSubmitting(true); setErr("");
    try {
      await publicApi.post(`/public/incidents/${areaId}`, {
        area_id: areaId, type, description: description.trim(),
        priority: "normal", photo_url: photoUrl,
        reporter_name: name.trim() || null, reporter_contact: contact.trim() || null,
      });
      setSubmitted(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al enviar el reporte");
    } finally { setSubmitting(false); }
  };

  if (err && !info) {
    return <div className="min-h-screen p-6 flex items-center justify-center"><div className="lx-card max-w-md w-full text-center"><h2 className="font-heading font-bold text-xl">Error</h2><p className="text-sm text-lx-muted mt-2">{err}</p></div></div>;
  }
  if (!info) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-lx-primary border-t-transparent rounded-full animate-spin"/></div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5" style={{ background: "#10B98122" }}>
          <CheckCircle2 size={48} color="#10B981" strokeWidth={1.8}/>
        </div>
        <h1 className="font-heading font-bold text-3xl tracking-tight" data-testid="report-success">¡Reporte enviado!</h1>
        <p className="text-lx-muted mt-2 max-w-sm">Gracias. Limpio Express revisará tu reporte a la brevedad{contact ? " y se contactará contigo si es necesario." : "."}</p>
        <Link to={`/scan/${areaId}`} className="lx-btn lx-btn-secondary mt-6">Volver</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      <div className="border-b border-lx-border bg-white px-5 py-3 flex items-center gap-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#00AEEF" }}>
          <Sparkles size={16} color="#fff"/>
        </div>
        <div className="font-heading font-bold text-sm">Limpio<span className="text-lx-primary">Express</span></div>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-5">
        <div className="rounded-2xl p-5 text-white" style={{ background: "linear-gradient(135deg, #F59E0B, #DC8400)" }}>
          <div className="flex items-center gap-2"><AlertTriangle size={18}/><div className="text-xs uppercase tracking-widest opacity-90 font-bold">Reportar problema</div></div>
          <h1 className="font-heading font-bold text-2xl tracking-tight mt-1">{info.area.name}</h1>
          <div className="text-sm opacity-95 mt-1 flex items-center gap-1.5"><MapPin size={13}/>{info.location?.name} · {info.client?.name}</div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-5">
          <div>
            <label className="lx-label">Tipo de problema</label>
            <select className="lx-input" value={type} onChange={(e)=>setType(e.target.value)} data-testid="report-type">
              {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Descripción <span className="text-lx-error">*</span></label>
            <textarea
              className="lx-input" rows={4}
              placeholder="Describe el problema con detalle (mín. 10 caracteres)…"
              value={description}
              onChange={(e)=>setDescription(e.target.value)}
              required
              data-testid="report-description"
            />
            <p className="text-xs text-lx-muted mt-1">{description.length}/10 caracteres mínimos</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="lx-label">Tu nombre <span className="text-lx-muted font-normal">(opcional)</span></label>
              <input className="lx-input" value={name} onChange={(e)=>setName(e.target.value)} data-testid="report-name"/>
            </div>
            <div>
              <label className="lx-label">Teléfono o email <span className="text-lx-muted font-normal">(opcional)</span></label>
              <input className="lx-input" value={contact} onChange={(e)=>setContact(e.target.value)} data-testid="report-contact" placeholder="Por si quieres seguimiento"/>
            </div>
          </div>
          <div>
            <label className="lx-label">Foto <span className="text-lx-muted font-normal">(opcional)</span></label>
            {photoPreview ? (
              <div className="relative inline-block">
                <img src={photoPreview} alt="Reporte" className="rounded-lg max-h-48 border border-lx-border"/>
                <button type="button" onClick={removePhoto} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"><X size={14}/></button>
              </div>
            ) : (
              <label className="lx-btn lx-btn-secondary lx-btn-mobile cursor-pointer" data-testid="report-take-photo">
                <Camera size={18}/> Tomar foto
                <input ref={inputRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto}/>
              </label>
            )}
            {uploading && <div className="text-xs text-lx-muted mt-2">Subiendo foto…</div>}
          </div>
          {err && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">{err}</div>}
        </form>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-lx-border p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
        <div className="max-w-lg mx-auto flex gap-2">
          <Link to={`/scan/${areaId}`} className="lx-btn lx-btn-secondary flex-shrink-0">Cancelar</Link>
          <button
            type="submit" onClick={submit} disabled={submitting || uploading || description.trim().length < 10}
            className="lx-btn lx-btn-primary lx-btn-mobile flex-1"
            data-testid="submit-report-button"
          >
            {submitting ? "Enviando…" : "Enviar reporte"}
          </button>
        </div>
      </div>
    </div>
  );
}
